import moment from 'moment';
import React from 'react';

import {Text, TextField} from '@albert-io/atomic';
import {dateRangeOptionsWithoutAllTime, RANGES} from 'generic/SelectableDateRange/shared';
import {getNumberWithSuffix} from 'lib/stringUtils';

import {ScopeSelect} from './ScopeSelect';
import type {ClassOverview} from './useClassOverview';

const DATE_OPTIONS = [
  ...(dateRangeOptionsWithoutAllTime.toJS() as Array<{id: string; name: string}>).map((option) => ({
    value: option.id,
    label: option.name
  })),
  {value: RANGES.CUSTOM, label: RANGES.CUSTOM}
];

// School year runs Jul 1 → Jun 30; offsetYears 0 = this year, -1 = last year.
const schoolYearRange = (offsetYears: number) => {
  const now = moment();
  const startYear = (now.month() < 6 ? now.year() - 1 : now.year()) + offsetYears;
  return {
    start: moment({year: startYear, month: 6, day: 1}).startOf('day').valueOf(),
    end: moment({year: startYear + 1, month: 5, day: 30})
      .endOf('day')
      .valueOf()
  };
};

// Resolve a preset id to a concrete range against the current date (mirrors SelectableDateRange).
const resolvePresetRange = (value: string): {start: number; end: number} | null => {
  switch (value) {
    case RANGES.THIS_WEEK:
      return {start: moment().startOf('week').valueOf(), end: moment().endOf('week').valueOf()};
    case RANGES.THIS_MONTH:
      return {start: moment().startOf('month').valueOf(), end: moment().endOf('month').valueOf()};
    case RANGES.LAST_WEEK:
      return {
        start: moment().subtract(1, 'week').startOf('week').valueOf(),
        end: moment().subtract(1, 'week').endOf('week').valueOf()
      };
    case RANGES.LAST_MONTH:
      return {
        start: moment().subtract(1, 'month').startOf('month').valueOf(),
        end: moment().subtract(1, 'month').endOf('month').valueOf()
      };
    case RANGES.THIS_SCHOOL_YEAR:
      return schoolYearRange(0);
    case RANGES.LAST_SCHOOL_YEAR:
      return schoolYearRange(-1);
    default:
      return null;
  }
};

const toDateInput = (ms: number | null): string =>
  ms == null ? '' : moment(ms).format('YYYY-MM-DD');

interface Props {
  overview: ClassOverview;
}

// Subject / Attempt / Date scope controls; all drive the students_v2 query via the hook setters.
export const ClassOverviewScopeBar = ({overview}: Props) => {
  const {
    scope,
    subjectOptions,
    students,
    setSubjectIds,
    setAttemptNumber,
    setDateRange,
    setDateMode
  } = overview;

  const maxAttempts = students?.getMaxNumberOfAttempts() ?? 0;
  const attemptOptions = Array.from({length: maxAttempts}, (unused, index) => ({
    value: String(index + 1),
    label: `${getNumberWithSuffix(index + 1)} attempt`
  }));

  const isCustomRange = scope.dateMode === RANGES.CUSTOM;
  const customRangeInvalid =
    isCustomRange &&
    (scope.startDate == null || scope.endDate == null || scope.startDate >= scope.endDate);

  const handleDateOption = (value: string) => {
    if (value === RANGES.CUSTOM) {
      setDateMode(RANGES.CUSTOM);
      // Seed Custom with the legacy default (last month) when there's no range yet.
      if (scope.startDate == null || scope.endDate == null) {
        setDateRange(
          moment().subtract(1, 'month').startOf('day').valueOf(),
          moment().endOf('day').valueOf()
        );
      }
      return;
    }
    const range = resolvePresetRange(value);
    if (range) {
      setDateMode(value);
      setDateRange(range.start, range.end);
    }
  };

  // Editing a date input directly switches to a Custom range, mirroring the legacy
  // SelectableDateRange (which flips to Custom when its DateRange is manipulated).
  const handleStartDateChange = (value: string) => {
    setDateMode(RANGES.CUSTOM);
    setDateRange(value ? moment(value).startOf('day').valueOf() : null, scope.endDate);
  };
  const handleEndDateChange = (value: string) => {
    setDateMode(RANGES.CUSTOM);
    setDateRange(scope.startDate, value ? moment(value).endOf('day').valueOf() : null);
  };

  return (
    <div className='gap-x2 border-border-primary pb-x4-half s:flex-row s:flex-wrap s:items-end flex flex-col border-b [&_input]:rounded-none! [&_input::-webkit-calendar-picker-indicator]:cursor-pointer!'>
      <ScopeSelect
        label='Subject'
        name='overview-subject'
        value={scope.subjectIds[0] ?? ''}
        options={[{value: '', label: 'All Subjects'}, ...subjectOptions]}
        onChange={(value) => setSubjectIds(value ? [value] : [])}
      />
      <ScopeSelect
        label='Attempt Number'
        name='overview-attempt'
        value={scope.attemptNumber == null ? '' : String(scope.attemptNumber)}
        options={[{value: '', label: 'Most Recent'}, ...attemptOptions]}
        onChange={(value) => setAttemptNumber(value ? Number(value) : null)}
      />
      <ScopeSelect
        label='Date'
        name='overview-date'
        value={scope.dateMode}
        options={DATE_OPTIONS}
        onChange={handleDateOption}
      />
      <TextField
        label='Start date'
        labelTextClassName='font-bold!'
        type='date'
        name='overview-start-date'
        value={toDateInput(scope.startDate)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleStartDateChange(event.target.value)
        }
      />
      <TextField
        label='End date'
        labelTextClassName='font-bold!'
        type='date'
        name='overview-end-date'
        value={toDateInput(scope.endDate)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleEndDateChange(event.target.value)
        }
      />
      {customRangeInvalid && (
        <Text size='xs' color='negative' className='w-full'>
          Please enter a valid date range
        </Text>
      )}
    </div>
  );
};
